<template>
  <section class="portfolio-section">
    <div class="container">

      <div class="row justify-content-center">
        <div class="col-lg-7 text-center">
          <span class="section-label" v-reveal>{{ $t('portfolio.label') }}</span>
          <h2 class="section-title" v-reveal="100">{{ $t('portfolio.title') }}</h2>
          <p class="section-subtitle" v-reveal="180">{{ $t('portfolio.subtitle') }}</p>
        </div>
      </div>

      <div class="row g-4 mt-2">
        <div
          v-for="(project, index) in featuredProjects"
          :key="project.id"
          class="col-lg-4 col-md-4"
          v-reveal="index * 120"
        >
          <div class="project-card" v-tilt="4">
            <a
              :href="project.url || null"
              :target="project.url ? '_blank' : null"
              :rel="project.url ? 'noopener noreferrer' : null"
              class="project-image"
              :class="[project.gradient, { 'no-link': !project.url }]"
            >
              <img
                v-if="project.src"
                :src="project.src"
                :alt="project.title"
                class="project-screenshot"
                @error="e => e.target.style.display='none'"
              />
              <div class="project-overlay">
                <i class="fas fa-external-link-alt"></i>
                <span v-if="project.url">Bekijk live</span>
              </div>
            </a>
            <div class="project-body">
              <h3>{{ project.title }}</h3>
              <p>{{ project.description[locale] }}</p>
              <div class="project-tags">
                <span v-for="tag in project.tags" :key="tag">{{ tag }}</span>
              </div>
              <router-link to="/portfolio" class="btn-project">
                {{ $t('portfolio.view_project') }} <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-5">
        <div class="col-12 text-center" v-reveal>
          <router-link to="/portfolio" class="btn-all-projects">
            {{ $t('portfolio.view_all') }} <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { featuredProjects } from '@/data/projects'

const { locale } = useI18n()
</script>

<style scoped>
.portfolio-section {
  padding: 6.5rem 0 5.5rem;
  background-color: transparent;
}

.section-label {
  display: inline-block;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.8rem;
  color: var(--accent);
  letter-spacing: 1px;
  margin-bottom: 0.875rem;
}

.section-title {
  font-family: 'Jersey 15', sans-serif;
  font-size: 2.75rem;
  color: var(--text);
  font-weight: 400;
  margin-bottom: 1rem;
  letter-spacing: 0;
}

.section-subtitle {
  color: var(--text-muted);
  font-size: 1.05rem;
  line-height: 1.7;
  margin-bottom: 0;
}

.project-card {
  background: linear-gradient(180deg, #151515, #0e0e0e);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s, border-color 0.3s;
  transform-style: preserve-3d;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.project-card:hover {
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.6), 0 0 40px var(--accent-tint);
  border-color: var(--accent-line);
}

.project-image { height: 200px; position: relative; overflow: hidden; display: block; cursor: pointer; }
.project-image.no-link { cursor: default; pointer-events: none; }

/* Luxe light-sweep across the screenshot on hover */
.project-image::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -80%;
  width: 55%;
  background: linear-gradient(105deg, transparent, rgba(255, 255, 255, 0.18), transparent);
  transform: skewX(-16deg);
  transition: left 0.65s ease;
  pointer-events: none;
  z-index: 1;
}
.project-card:hover .project-image::after { left: 130%; }

.project-screenshot {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.4s ease;
}

.project-card:hover .project-screenshot {
  transform: scale(1.04);
}

/* On-brand fallbacks, only visible when a screenshot fails to load */
.gradient-1 { background: linear-gradient(135deg, #1a1206 0%, #7a5416 50%, #FAA82B 100%); }
.gradient-2 { background: linear-gradient(135deg, #0e0e0e 0%, #4a3510 50%, #e08a12 100%); }
.gradient-3 { background: linear-gradient(135deg, #201607 0%, #8a6018 50%, #ffc369 100%); }

.project-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s;
}

.project-overlay i { font-size: 1.4rem; color: #fff; }
.project-overlay span { font-size: 0.8rem; color: rgba(255,255,255,0.85); font-weight: 600; letter-spacing: 0.5px; }
.project-card:hover .project-overlay { opacity: 1; }

.project-body {
  padding: 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.project-body h3 {
  font-size: 1.15rem;
  color: var(--text);
  font-weight: 700;
  margin-bottom: 0.625rem;
}

.project-body p {
  color: var(--text-muted);
  font-size: 0.92rem;
  line-height: 1.7;
  margin-bottom: 1.25rem;
  flex: 1;
}

.project-tags { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1.5rem; }

.project-tags span {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 0.7rem;
  color: var(--text-soft);
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 0.2rem 0.55rem;
}

.btn-project {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
  transition: gap 0.2s, color 0.2s;
}

.btn-project:hover { color: var(--accent-soft); gap: 0.65rem; text-decoration: none; }
.btn-project i { font-size: 0.75rem; transition: transform 0.2s; }
.btn-project:hover i { transform: translateX(2px); }

.btn-all-projects {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.9rem 2.1rem;
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.2s, color 0.2s, transform 0.15s, box-shadow 0.2s;
}

.btn-all-projects:hover {
  border-color: var(--accent);
  color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 6px 24px var(--accent-tint);
  text-decoration: none;
}

.btn-all-projects i { font-size: 0.8rem; transition: transform 0.2s; }
.btn-all-projects:hover i { transform: translateX(3px); }

@media (max-width: 991px) { .portfolio-section { padding: 5rem 0 4rem; } }
@media (max-width: 767px) {
  .portfolio-section { padding: 4rem 0 3rem; }
  .section-title { font-size: 1.875rem; }
  .section-subtitle { font-size: 0.95rem; }
  .project-image { height: 175px; }
  .project-body { padding: 1.375rem 1.5rem; }
}
</style>
